import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowRight,
  Sparkles,
  Mic2,
  Repeat2,
  CalendarClock,
  RotateCcw,
  Check,
  X as XIcon,
  Zap,
  ChevronDown,
  Rocket,
  Briefcase,
  GraduationCap,
  type LucideIcon,
} from "lucide-react";
import { LandingHeader, LandingFooter } from "../components/landing-chrome";
import {
  HeroProductPreview,
  VoicePreview,
  PlatformOutputsPreview,
  AutomationPreview,
} from "../components/landing-visuals";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <DotGridBg />
      <LandingHeader />
      <Hero />
      <LogosStrip />
      <FeatureSplit
        eyebrow="Voice-aware AI"
        title="Train your style. Echo it everywhere."
        body="Paste 3–10 of your past posts. BrandEcho extracts the structural signature — sentence rhythm, openers, mood — and grounds every future generation in your voice. No generic 'tones' to choose from."
        bullets={[
          "Quality scoring (weak / fair / good / strong)",
          "Train multiple voices for different personas",
          "Voice flows into Generate, Hooks, Evergreen, Auto-plug",
        ]}
        visual={<VoicePreview />}
        Icon={Mic2}
      />
      <FeatureSplit
        reversed
        eyebrow="One paste, five outputs"
        title="Native variants for every channel."
        body="Each platform has its own physics — character limits, thread structure, reading mode, hooks. BrandEcho generates each variant from scratch, not by truncation. One credit covers all five."
        bullets={[
          "X auto-thread chains with reply structure",
          "LinkedIn long-form with line-spacing optimization",
          "Newsletter, Bluesky, and Short-video script",
        ]}
        visual={<PlatformOutputsPreview />}
        Icon={Sparkles}
      />
      <FeatureSplit
        eyebrow="Automation, the smart way"
        title="Recycle without repeating yourself."
        body="Schedule, queue, and auto-recycle — but never with the same words twice. Evergreen rewrites with AI on every cadence. Auto-retweet costs zero AI credit. Auto-plug ships voice-matched follow-ups."
        bullets={[
          "Recurring queue slots with categories",
          "Evergreen AI-rewrite cadence",
          "X auto-retweet on like-threshold",
          "Voice-matched auto-plug follow-ups",
        ]}
        visual={<AutomationPreview />}
        Icon={Repeat2}
      />
      <UseCases />
      <Comparison />
      <Stats />
      <Quote />
      <HomeFaq />
      <FinalCta />
      <LandingFooter />
    </div>
  );
}

function UseCases() {
  const cards = [
    {
      Icon: Rocket,
      title: "Solo founder",
      pain: "I need a daily presence on X and LinkedIn but I can't write the same post twice.",
      gain: "Paste one weekly insight, get five native variants, schedule the week in 8 minutes.",
      tags: ["X", "LinkedIn", "Newsletter"],
    },
    {
      Icon: GraduationCap,
      title: "Course / newsletter creator",
      pain: "Every newsletter issue should also drive social, but rewriting kills my Tuesday.",
      gain: "BrandEcho rewrites the same idea into a thread, a LinkedIn post, and a Bluesky skeet — all linking back.",
      tags: ["Newsletter", "X thread", "Auto-plug"],
    },
    {
      Icon: Briefcase,
      title: "Agency / small team",
      pain: "Each client has a different voice. Switching brains all day costs us hours.",
      gain: "Train one voice profile per client. Schedule a quarter ahead. Recycle evergreens with AI variation.",
      tags: ["Multi-voice", "Evergreen", "Categories"],
    },
  ];
  return (
    <section className="border-y border-ink-200/60 bg-ink-50/60">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="text-center">
          <span className="badge-brand">Built for</span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
            Where it earns its keep
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-600">
            Three workflows where BrandEcho compounds the most.
          </p>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {cards.map((c) => (
            <div
              key={c.title}
              className="group flex flex-col rounded-2xl border border-ink-200/70 bg-white p-6 shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-sm">
                <c.Icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-ink-900">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-500">
                <span className="font-medium text-ink-700">"{c.pain}"</span>
              </p>
              <p className="mt-3 text-sm leading-relaxed text-ink-700">{c.gain}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {c.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-md bg-ink-100 px-2 py-0.5 text-2xs font-medium text-ink-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const rows: { label: string; manual: boolean; basic: boolean; brand: boolean }[] = [
    { label: "Native variant per platform (not truncation)", manual: false, basic: false, brand: true },
    { label: "Voice-aware AI grounded in your past posts", manual: true, basic: false, brand: true },
    { label: "Auto-thread chains with proper reply structure", manual: false, basic: false, brand: true },
    { label: "Recycle evergreens without word-for-word repeat", manual: false, basic: false, brand: true },
    { label: "X auto-retweet on engagement threshold", manual: false, basic: true, brand: true },
    { label: "Voice-matched auto-plug follow-ups", manual: false, basic: false, brand: true },
    { label: "Edge-fast publishing (Cloudflare workers)", manual: false, basic: false, brand: true },
    { label: "Bring your own AI key (BYOK)", manual: false, basic: false, brand: true },
    { label: "7-day free trial · cancel anytime", manual: false, basic: false, brand: true },
  ];
  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="text-center">
          <span className="badge-brand">How we compare</span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
            Not another scheduler. <span className="text-gradient">Not even close.</span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-ink-600">
            Most tools either schedule what you write, or write generic AI slop.
            BrandEcho does both — natively per platform, in your voice.
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-sm">
          <div className="grid grid-cols-4 border-b border-ink-200/70 bg-ink-50/70">
            <div className="p-4 text-2xs font-semibold uppercase tracking-wider text-ink-500">
              Capability
            </div>
            <div className="p-4 text-center text-2xs font-semibold uppercase tracking-wider text-ink-500">
              Doing it manually
            </div>
            <div className="p-4 text-center text-2xs font-semibold uppercase tracking-wider text-ink-500">
              Other schedulers
            </div>
            <div className="p-4 text-center text-2xs font-semibold uppercase tracking-wider text-brand-700 bg-gradient-to-b from-brand-50/60 to-transparent">
              BrandEcho
            </div>
          </div>
          {rows.map((r, i) => (
            <div
              key={r.label}
              className={`grid grid-cols-4 ${i % 2 === 0 ? "" : "bg-ink-50/40"}`}
            >
              <div className="px-4 py-3 text-sm text-ink-800">{r.label}</div>
              <div className="px-4 py-3 text-center">
                <CompareCell ok={r.manual} />
              </div>
              <div className="px-4 py-3 text-center">
                <CompareCell ok={r.basic} />
              </div>
              <div className="px-4 py-3 text-center bg-gradient-to-b from-brand-50/40 to-transparent">
                <CompareCell ok={r.brand} highlight />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompareCell({ ok, highlight }: { ok: boolean; highlight?: boolean }) {
  if (ok) {
    return (
      <span
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${highlight ? "bg-brand-gradient text-white shadow-sm" : "bg-emerald-100 text-emerald-700"}`}
      >
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink-100 text-ink-400">
      <XIcon className="h-4 w-4" strokeWidth={2.5} />
    </span>
  );
}

function HomeFaq() {
  const items = [
    {
      q: "Is BrandEcho just another scheduler?",
      a: "No. Most tools schedule what you write — BrandEcho writes platform-native variants in your voice, then schedules them. Think Hypefury × ChatGPT, but the AI is grounded in YOUR style.",
    },
    {
      q: "Will my posts sound like AI slop?",
      a: "Voice-aware grounding is the whole point. You paste 3-10 of your past posts, we extract your structural fingerprint (cadence, openers, vocabulary, signature patterns), and every future generation hangs on that voice. Quality scoring tells you when training is strong.",
    },
    {
      q: "Do I need an OpenAI key?",
      a: "No. Paid plans include AI usage. If you bring your own OpenAI / Anthropic key (Settings → AI keys), you only pay platform credits and never burn through ours.",
    },
    {
      q: "What about images? videos? newsletters?",
      a: "Images: yes — attach up to 4 per post (X, Bluesky, LinkedIn). Newsletter & short-video are SCRIPT-ONLY — we generate the body, you copy/download to Substack/Beehiiv/TikTok. We don't pretend to be a video host.",
    },
    {
      q: "Will my content be used to train AI?",
      a: "No. Never. Your posts, voice profiles, and analytics live in your private Cloudflare D1 database. We don't sell, share, or fine-tune on your data.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes. No contracts, no minimum term. Cancel from Settings → Billing — your plan stays active until the end of the current period and you're never charged again.",
    },
    {
      q: "Which platforms can I publish to?",
      a: "X (Twitter), Bluesky, and LinkedIn directly. Newsletter and short-video produce a script for you to paste — we don't claim to send email or upload videos for you.",
    },
    {
      q: "How fast is it?",
      a: "It's a Cloudflare Workers stack — your dashboard loads under 50ms globally, generations take 6-12s, and publishes hit the platform APIs from the edge.",
    },
  ];
  return (
    <section className="bg-ink-50/60 border-y border-ink-200/60">
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="text-center">
          <span className="badge-brand">FAQ</span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
            Questions, answered up front
          </h2>
        </div>
        <div className="mt-10 space-y-3">
          {items.map((it) => (
            <FaqItem key={it.q} q={it.q} a={it.a} />
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-ink-500">
          Still wondering something?{" "}
          <a
            href="mailto:hello@brandecho.app"
            className="font-medium text-brand-600 hover:underline"
          >
            hello@brandecho.app
          </a>
        </p>
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-ink-200/70 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-ink-50/60"
      >
        <span className="text-sm font-semibold text-ink-900 sm:text-base">
          {q}
        </span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-ink-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="border-t border-ink-200/60 px-5 py-4 text-sm leading-relaxed text-ink-700">
          {a}
        </div>
      ) : null}
    </div>
  );
}

function DotGridBg() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 opacity-[0.4]"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(15,16,20,0.07) 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }}
    />
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 -top-40 h-[640px]">
        <div className="absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-brand-gradient-soft blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 sm:pb-24 sm:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-6">
            <span className="badge-brand">
              <Sparkles className="h-3 w-3" />
              Voice-aware · Multi-platform · Edge-fast
            </span>
            <h1 className="mt-6 text-[44px] font-bold leading-[1.04] tracking-tight text-ink-900 sm:text-[58px] md:text-[68px]">
              Write once,
              <br />
              <span className="text-gradient">echo everywhere.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-600 sm:text-lg">
              Turn one long-form post into native variants for X, Bluesky,
              LinkedIn, Newsletter, and short-video — written in your voice and
              scheduled automatically.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" className="btn-brand btn-lg no-underline">
                Start 7-day trial
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/features" className="btn-secondary btn-lg no-underline">
                Tour every feature
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-500">
              <Trust label="7-day free trial" />
              <Trust label="Cancel anytime" />
              <Trust label="Bring your own AI key" />
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[28px] bg-brand-gradient opacity-[0.08] blur-2xl" />
              <HeroProductPreview />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Check className="h-3.5 w-3.5 text-brand-500" />
      {label}
    </span>
  );
}

function LogosStrip() {
  return (
    <section className="border-y border-ink-200/60 bg-white/60 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <p className="text-center text-2xs font-semibold uppercase tracking-[0.2em] text-ink-400">
          Publishes natively to
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 sm:gap-x-12">
          <PlatformBadge label="X" />
          <PlatformBadge label="Bluesky" />
          <PlatformBadge label="LinkedIn" />
          <PlatformBadge label="Newsletter" />
          <PlatformBadge label="Short-video" />
        </div>
      </div>
    </section>
  );
}

function PlatformBadge({ label }: { label: string }) {
  return (
    <span className="text-base font-semibold tracking-tight text-ink-400 transition-colors hover:text-ink-700">
      {label}
    </span>
  );
}

interface FeatureSplitProps {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  visual: React.ReactNode;
  Icon: LucideIcon;
  reversed?: boolean;
}

function FeatureSplit({
  eyebrow,
  title,
  body,
  bullets,
  visual,
  Icon,
  reversed,
}: FeatureSplitProps) {
  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
        <div
          className={`grid items-center gap-12 lg:grid-cols-12 lg:gap-16 ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}
        >
          <div className="lg:col-span-5">
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-sm">
              <Icon className="h-5 w-5" />
            </div>
            <p className="text-2xs font-semibold uppercase tracking-wider text-brand-600">
              {eyebrow}
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
              {title}
            </h2>
            <p className="mt-4 text-ink-600">{body}</p>
            <ul className="mt-6 space-y-2.5">
              {bullets.map((b) => (
                <li key={b} className="flex gap-2.5 text-sm text-ink-700">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-500" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[24px] bg-brand-gradient opacity-[0.06] blur-2xl" />
              <div className="relative">{visual}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { v: "5", l: "Native platforms per credit" },
    { v: "13", l: "Modules across the stack" },
    { v: "<50ms", l: "Edge response, globally" },
    { v: "7d", l: "Free trial, cancel anytime" },
  ];
  return (
    <section className="bg-ink-900 text-white">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {items.map((it) => (
            <div key={it.l} className="text-center">
              <p className="text-4xl font-bold tracking-tight sm:text-5xl">
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  {it.v}
                </span>
              </p>
              <p className="mt-2 text-xs font-medium uppercase tracking-wider text-ink-400 sm:text-sm">
                {it.l}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Quote() {
  return (
    <section className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-6">
      <div className="mx-auto h-1 w-12 rounded-full bg-brand-gradient" />
      <p className="mt-8 text-2xl font-medium leading-relaxed tracking-tight text-ink-900 sm:text-3xl">
        "BrandEcho is what Hypefury would look like if it had been built in
        2026, edge-native, and respected your voice."
      </p>
      <p className="mt-6 text-sm text-ink-500">
        — Designed for creators who write once and grow on every platform
      </p>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden border-t border-ink-200/60">
      <div className="absolute inset-0 bg-brand-gradient-soft" />
      <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-24">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-md">
          <Zap className="h-6 w-6" />
        </div>
        <h2 className="mt-6 text-3xl font-bold tracking-tight text-ink-900 sm:text-5xl">
          Ship your first <span className="text-gradient">cross-platform</span>
          <br />
          post today.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-ink-600">
          7 days, completely free. Connect one social account and you'll
          publish to all five formats in under 10 minutes.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link to="/login" className="btn-brand btn-lg no-underline">
            Start 7-day trial
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/pricing" className="btn-secondary btn-lg no-underline">
            See pricing
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-ink-500">
          <Trust label="7-day free trial" />
          <Trust label="Cancel anytime" />
          <Trust label="Connect X / Bluesky / LinkedIn" />
        </div>
      </div>
    </section>
  );
}

function CalendarPreview() {
  return <CalendarClock className="h-4 w-4" />;
}
function RotatePreview() {
  return <RotateCcw className="h-4 w-4" />;
}
void CalendarPreview;
void RotatePreview;
